<template>
  <div class="image-explainer" ref="container">
    <canvas ref="canvas" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted, computed } from 'vue';

const props = defineProps<{
  src: string;
  rects: Record<string, { x: number; y: number; w: number; h: number }>;
  focusOn?: string;
}>();

const emit = defineEmits<{
  (e: 'canvasResized'): void;
  (e: 'imageLoaded'): void;
}>();

const sections = computed(() => {
  return Object.entries(props.rects).reduce(
    (acc, [key, { x, y, w, h }]) => {
      acc[key] = { x: x + w / 2, y: y + h / 2, w, h };
      return acc;
    },
    {} as Record<string, { x: number; y: number; w: number; h: number }>
  );
});

const MAX_ZOOM_FACTOR = 1.2;
const FRICTION = 0.88;
const ACCELERATION = 0.01;

const container = ref<HTMLElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
let ctx: CanvasRenderingContext2D | null = null;
const img = new Image();

// Transform state
let scale = 1;
let targetScale = 1;
let offsetX = 0;
let offsetY = 0;
let targetOffsetX = 0;
let targetOffsetY = 0;
let alpha = 0;
let targetAlpha = 0;

// Store the last focused rectangle for fade-out
let lastFocusRect: { x: number; y: number; w: number; h: number } | null = null;

// Velocities
let scaleVel = 0;
let velX = 0;
let velY = 0;
let alphaVel = 0;

// Scales
let coverScale = 1;
let maxScale = 1;

let animationFrameId: number;

onMounted(() => {
  if (!canvas.value) return;
  ctx = canvas.value.getContext('2d');

  img.crossOrigin = 'Anonymous';
  img.src = props.src;

  img.onload = () => {
    initialize();
    emit('imageLoaded');
  };
  img.onerror = () => console.error('Image failed to load');

  window.addEventListener('resize', resizeCanvas);
});

onUnmounted(() => {
  window.removeEventListener('resize', resizeCanvas);
  cancelAnimationFrame(animationFrameId);
});

function initialize() {
  resizeCanvas();
  calculateCoverAndMaxScale();
  scale = targetScale = coverScale;
  offsetX = targetOffsetX = -(img.width / 2);
  offsetY = targetOffsetY = 0;
  alpha = 0;
  targetAlpha = 0;
  animate();
}

function resizeCanvas() {
  if (!canvas.value || !container.value) return;

  const dpr = window.devicePixelRatio;

  canvas.value.width = container.value.clientWidth * dpr;
  canvas.value.height = container.value.clientHeight * dpr;
  emit('canvasResized');
}

function calculateCoverAndMaxScale() {
  if (!canvas.value) return;
  const widthRatio = canvas.value.width / img.width;
  const heightRatio = canvas.value.height / img.height;
  coverScale = Math.max(widthRatio, heightRatio);
  maxScale = coverScale * MAX_ZOOM_FACTOR;
}

function focusOnRect(rectKey: string) {
  if (!img.complete || !sections.value[rectKey]) return;

  const { x: focusX, y: focusY } = sections.value[rectKey];
  const desiredScale = maxScale;
  targetScale = Math.max(coverScale, Math.min(desiredScale, maxScale));

  // limit the target scale by the size of the rect
  const { w } = props.rects[rectKey];
  const scaleW = canvas.value ? canvas.value.width / w : 0;
  targetScale = Math.min(targetScale, scaleW * 0.95);

  targetAlpha = 1; // fade in the gradient
  lastFocusRect = sections.value[rectKey]; // store the current rect

  const { clampedOffsetX, clampedOffsetY } = getClampedTargetOffset(focusX, focusY, targetScale);
  targetOffsetX = clampedOffsetX;
  targetOffsetY = clampedOffsetY;
}

function resetFocus() {
  if (!img.complete) return;
  const desiredScale = coverScale;
  const { clampedOffsetX } = getClampedTargetOffset(img.width / 2, img.height / 2, desiredScale);
  targetScale = desiredScale;
  targetOffsetX = clampedOffsetX;
  targetOffsetY = 0;
  targetAlpha = 0; // Fade out the gradient
  // Keep lastFocusRect until alpha reaches 0
}

function getClampedTargetOffset(fx: number, fy: number, s: number) {
  if (!canvas.value) return { clampedOffsetX: 0, clampedOffsetY: 0 };

  let offsetCandidateX = -fx;
  let offsetCandidateY = -fy;

  const halfW = canvas.value.width / 2;
  const halfH = canvas.value.height / 2;

  const minOffsetX = (canvas.value.width - halfW) / s - img.width;
  const maxOffsetX = -halfW / s;
  offsetCandidateX = Math.max(minOffsetX, Math.min(maxOffsetX, offsetCandidateX));

  const minOffsetY = (canvas.value.height - halfH) / s - img.height;
  const maxOffsetY = -halfH / s;
  offsetCandidateY = Math.max(minOffsetY, Math.min(maxOffsetY, offsetCandidateY));

  return { clampedOffsetX: offsetCandidateX, clampedOffsetY: offsetCandidateY };
}

function drawFocusGradientCutout(x: number, y: number, rectW: number, rectH: number) {
  if (!ctx) return;

  const offscreen = document.createElement('canvas');
  offscreen.width = img.width;
  offscreen.height = img.height;
  const offCtx = offscreen.getContext('2d');
  if (!offCtx) return;

  const radius = Math.sqrt(img.width ** 2 + img.height ** 2) * 0.4;
  const grad = offCtx.createRadialGradient(x, y, 0, x, y, radius);
  grad.addColorStop(0, `rgba(0, 0, 0, 0)`);
  grad.addColorStop(0.5, `rgba(0, 0, 0, ${0.4 * alpha})`);
  grad.addColorStop(1, `rgba(0, 0, 0, ${0.5 * alpha})`);
  offCtx.fillStyle = grad;
  offCtx.fillRect(0, 0, img.width, img.height);

  offCtx.globalCompositeOperation = 'destination-out';
  offCtx.fillStyle = 'black';

  const cornerRadius = 10;
  offCtx.beginPath();
  offCtx.moveTo(x - rectW / 2 + cornerRadius, y - rectH / 2);
  offCtx.arcTo(x + rectW / 2, y - rectH / 2, x + rectW / 2, y + rectH / 2, cornerRadius);
  offCtx.arcTo(x + rectW / 2, y + rectH / 2, x - rectW / 2, y + rectH / 2, cornerRadius);
  offCtx.arcTo(x - rectW / 2, y + rectH / 2, x - rectW / 2, y - rectH / 2, cornerRadius);
  offCtx.arcTo(x - rectW / 2, y - rectH / 2, x + rectW / 2, y - rectH / 2, cornerRadius);
  offCtx.closePath();
  offCtx.fill();

  ctx.drawImage(offscreen, 0, 0);
}

function animate() {
  if (!ctx || !canvas.value) return;

  // Update velocities
  scaleVel += (targetScale - scale) * ACCELERATION;
  velX += (targetOffsetX - offsetX) * ACCELERATION;
  velY += (targetOffsetY - offsetY) * ACCELERATION;
  alphaVel += (targetAlpha - alpha) * ACCELERATION;

  // Apply friction
  scaleVel *= FRICTION;
  velX *= FRICTION;
  velY *= FRICTION;
  alphaVel *= FRICTION;

  // Update transform
  scale += scaleVel;
  offsetX += velX;
  offsetY += velY;
  alpha += alphaVel;

  // Clamp boundaries
  scale = Math.max(coverScale, Math.min(maxScale, scale));
  alpha = Math.max(0, Math.min(1, alpha));

  if (scale === coverScale || scale === maxScale) scaleVel *= -0.5;
  if (alpha === 0 || alpha === 1) alphaVel *= -0.5;

  const halfW = canvas.value.width / 2;
  const halfH = canvas.value.height / 2;
  const minOffsetX = (canvas.value.width - halfW) / scale - img.width;
  const maxOffsetX = -halfW / scale;
  const minOffsetY = (canvas.value.height - halfH) / scale - img.height;
  const maxOffsetY = -halfH / scale;

  offsetX = Math.max(minOffsetX, Math.min(maxOffsetX, offsetX));
  offsetY = Math.max(minOffsetY, Math.min(maxOffsetY, offsetY));
  if (offsetX === minOffsetX || offsetX === maxOffsetX) velX *= -0.5;
  if (offsetY === minOffsetY || offsetY === maxOffsetY) velY *= -0.5;

  // Draw
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);
  ctx.save();
  ctx.translate(canvas.value.width / 2, canvas.value.height / 2);
  ctx.scale(scale, scale);
  ctx.translate(offsetX, offsetY);
  ctx.drawImage(img, 0, 0);

  // Draw gradient if focusing or fading out
  if (props.focusOn && sections.value[props.focusOn]) {
    const { x, y, w, h } = sections.value[props.focusOn];
    drawFocusGradientCutout(x, y, w, h);
  } else if (alpha > 0 && lastFocusRect) {
    // Continue drawing the last focused rectangle during fade-out
    const { x, y, w, h } = lastFocusRect;
    drawFocusGradientCutout(x, y, w, h);
    // Clear lastFocusRect when alpha reaches 0
    if (alpha === 0) lastFocusRect = null;
  }

  ctx.restore();
  animationFrameId = requestAnimationFrame(animate);
}

watch(
  () => props.focusOn,
  (newFocus) => {
    if (newFocus && sections.value[newFocus]) {
      focusOnRect(newFocus); // Fade in
    } else {
      resetFocus(); // Fade out
    }
  }
);
</script>

<style scoped>
.image-explainer {
  width: 100%;
  min-height: 500px;
  /* height: 100%; */
  position: static;
  overflow: hidden;
}

canvas {
  width: 100%;
  height: 100%;
}
</style>
